import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Hero from '../components/Hero';
import NarayanaWorkGrid from '../components/NarayanaWorkGrid';
import HealofyGallery from '../components/HealofyGallery';
import { ArrowUpRight } from 'lucide-react';

const Home = () => {
  const [data, setData] = useState({
    profile: null,
    projects: [],
    experience: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
        
        // Use Promise.allSettled to handle potential API failures gracefully
        const results = await Promise.allSettled([
          axios.get(`${backendUrl}/api/profile`),
          axios.get(`${backendUrl}/api/projects`),
          axios.get(`${backendUrl}/api/experience`)
        ]);

        const profile = results[0].status === 'fulfilled' ? results[0].value.data : null;
        const projects = results[1].status === 'fulfilled' ? results[1].value.data : [];
        const experience = results[2].status === 'fulfilled' ? results[2].value.data : [];

        setData({ profile, projects, experience });
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-pastel-bg flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-pastel-bg min-h-screen text-neutral-900 font-sans">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-pastel-bg/95 backdrop-blur-md">
        <div className="container mx-auto px-6 md:px-12 max-w-6xl h-16 flex items-center justify-between">
          <div className="flex items-center">
            <svg viewBox="0 0 48 36" fill="none" className="h-5 w-auto" aria-label="Sanket Daware">
              <path d="M 0,3  Q 12,-3  24,3  Q 36,9   48,3"  stroke="#1C1917" strokeWidth="1.5" strokeLinecap="round"/>
              <path d="M 0,8  Q 12,1   24,8  Q 36,15  48,8"  stroke="#1C1917" strokeWidth="1.5" strokeLinecap="round"/>
              <path d="M 0,13 Q 12,5   24,13 Q 36,21  48,13" stroke="#1C1917" strokeWidth="1.5" strokeLinecap="round"/>
              <path d="M 0,18 Q 12,9   24,18 Q 36,27  48,18" stroke="#1C1917" strokeWidth="1.5" strokeLinecap="round"/>
              <path d="M 0,23 Q 12,13  24,23 Q 36,33  48,23" stroke="#1C1917" strokeWidth="1.5" strokeLinecap="round"/>
              <path d="M 0,29 Q 12,19  24,29 Q 36,39  48,29" stroke="#1C1917" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>

          <div className="flex gap-8 text-sm font-medium text-neutral-500">
            {/* 
            <a href="/" className="hover:text-neutral-900 transition-colors">Home</a>
            <a href="#about" className="hover:text-neutral-900 transition-colors">About</a>
            */}
            <a 
              href="https://www.linkedin.com/in/sanket-daware/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:text-neutral-900 transition-colors"
            >
              LinkedIn
            </a>
          </div>
        </div>
        <div className="container mx-auto px-6 md:px-12 max-w-6xl"><div className="h-px bg-neutral-200" /></div>
      </nav>

      <main className="pt-16">
        <Hero profile={data.profile} />
        <div className="container mx-auto px-6 md:px-12 max-w-6xl"><div className="h-px bg-neutral-200" /></div>
        <NarayanaWorkGrid />
        <div className="container mx-auto px-6 md:px-12 max-w-6xl"><div className="h-px bg-neutral-200" /></div>
        <HealofyGallery />

        {/* Earlier work */}
        <div className="container mx-auto px-6 md:px-12 max-w-6xl py-16">
          <p className="text-xs font-mono text-neutral-400 uppercase tracking-widest mb-8">Earlier</p>
          <div className="flex flex-col gap-5">
            {[
              { company: 'Lenskart',            role: 'Product Designer',  dates: 'Jan – Mar 2019' },
              { company: 'BigApp Design Studio', role: 'Product Designer',  dates: '2018 – 2019' },
              { company: 'Pepper Square',        role: 'Product Designer',  dates: '2016 – 2017' },
            ].map(({ company, role, dates }) => (
              <div key={company} className="flex items-baseline justify-between">
                <div className="flex items-baseline gap-4">
                  <span className="text-sm font-semibold text-neutral-700 font-display">{company}</span>
                  <span className="text-xs font-mono text-neutral-400">{role}</span>
                </div>
                <span className="text-xs font-mono text-neutral-400">{dates}</span>
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="pb-16 mt-20">
        <div className="container mx-auto px-6 md:px-12 max-w-6xl mb-16"><div className="h-px bg-neutral-200" /></div>
        <div className="container mx-auto px-6 md:px-12 max-w-6xl">
            <div className="max-w-2xl">
                <h2 className="text-2xl font-semibold mb-6 font-array">Want to get in touch?</h2>
                <div className="flex flex-col gap-3 items-start">
                    <a href="mailto:dawaresanket001@gmail.com" className="text-neutral-500 hover:text-neutral-900 transition-colors flex items-center gap-2 text-sm">
                        Email <ArrowUpRight className="w-4 h-4" />
                    </a>
                    <a href="https://www.linkedin.com/in/sanket-daware/" target="_blank" rel="noopener noreferrer" className="text-neutral-500 hover:text-neutral-900 transition-colors flex items-center gap-2 text-sm">
                        LinkedIn <ArrowUpRight className="w-4 h-4" />
                    </a>
                </div>
            </div>

            <div className="mt-16 text-neutral-400 text-sm">
                © {new Date().getFullYear()} Sanket Daware.
            </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
